import { AfterViewInit, ContentChildren, Directive, ElementRef, OnDestroy, QueryList, Renderer2 } from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[thevatraDynamicTiles]'
})
export class DynamicTilesDirective implements AfterViewInit, OnDestroy {
  @ContentChildren('tile', { read: ElementRef }) tilesQueryList: QueryList<ElementRef>;

  private destroyed$ = new Subject<boolean>();

  constructor(private readonly elementRef: ElementRef, private readonly renderer2: Renderer2) {}

  ngAfterViewInit(): void {
    this.updateStylesForTiles(this.tilesQueryList);
    this.handleTilesQueryListChanges();
  }

  private handleTilesQueryListChanges(): void {
    this.tilesQueryList.changes
      .pipe(takeUntil(this.destroyed$))
      .subscribe((tilesQueryList: QueryList<ElementRef>) => this.updateStylesForTiles(tilesQueryList));
  }

  private updateStylesForTiles(tilesQueryList: QueryList<ElementRef>): void {
    const tiles = tilesQueryList.toArray();
    const style = this.getStylesForTile(tiles.length);

    tiles.forEach((tile: ElementRef) => {
      this.removeStylesFromTile(tile);
      this.addStylesForTile(tile, style);
    });
  }

  private removeStylesFromTile(tile: ElementRef): void {
    this.renderer2.removeStyle(tile.nativeElement, 'width');
    this.renderer2.removeStyle(tile.nativeElement, 'max-width');
    this.renderer2.removeStyle(tile.nativeElement, 'height');
  }

  private addStylesForTile(tile: ElementRef, style: { maxWidth?: string; width?: string; height?: string }): void {
    if (style.width) {
      this.renderer2.setStyle(tile.nativeElement, 'width', style.width);
    }

    if (style.maxWidth) {
      this.renderer2.setStyle(tile.nativeElement, 'max-width', style.maxWidth);
    }

    if (style.height) {
      this.renderer2.setStyle(tile.nativeElement, 'height', style.height);
    }
  }

  private getStylesForTile(tilesCount: number): { maxWidth?: string; width?: string; height?: string } {
    switch (true) {
      case tilesCount === 1:
        return {
          height: '100%',
          maxWidth: '100%'
        };
      case tilesCount === 2:
        return {
          width: '50%'
        };
      case tilesCount > 1 && tilesCount < 5:
        return {
          height: '50%',
          maxWidth: '50%'
        };
      case tilesCount > 3 && tilesCount < 10:
        return this.calculateStylesForTile(tilesCount, 3);
      case tilesCount >= 10 && tilesCount < 21:
        return this.calculateStylesForTile(tilesCount, 4);
      case tilesCount >= 21:
        return this.calculateStylesForTile(tilesCount, 5);
      default:
        return { height: '', maxWidth: '' };
    }
  }

  private calculateStylesForTile(tilesCount: number, numberOfTilesInRow: number): { height: string; maxWidth: string } {
    const tileWidth = 100 / numberOfTilesInRow;
    const tileHeight = 100 / Math.ceil(tilesCount / numberOfTilesInRow);

    return {
      height: `${tileHeight}%`,
      maxWidth: `${tileWidth}%`
    };
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
